import { ValueOptions } from '@mui/x-data-grid-premium';
import React, { useState } from 'react';
import { useNavigate } from 'react-router';
import { DataGrid, IGridColDef, Grid, Stack, IconButton, Button, Checkbox } from 'src/components';
import Card from 'src/components/Card';
import CardContent from 'src/components/CardContent';
import ParametreGrupEnum from 'src/enums/ParametreGrupEnum';
import { translate } from 'src/i18n/tHelper';
import VwKurumbonustanimDTO from 'src/models/KurumBonusTanim/VwKurumbonustanimDTO';
import IptalNedenSecim from 'src/parts/Genel/IptalNedenSecim';
import RecordInfo from 'src/parts/Genel/record.info';
import api from 'src/services/core/api';
import { useKurumBonusTanimGuncelleMutation, useKurumBonusTanimListesiQuery, useKurumBonusTanimSilMutation } from 'src/services/KurumBonusTanimlari/KurumBonusTanimlariApi';
import { useAppDispatch, useAppSelector } from 'src/store';
import { kurumBonusTanimlariSlice } from 'src/store/Slices/KurumBonusTanimlari/KurumBonusTanimlariSlice';

const translatePath = "pages|kurumBonusTanimlari";
const KurumBonusTanimlariListe = (): JSX.Element => {
    const state = useAppSelector(s => s.kurumBonusTanimlari);
    const parametre = useAppSelector(s => s.parameters.data);
    const dispatch = useAppDispatch();
    const navigate = useNavigate();

    const [iptal, setIptal] = useState<boolean>(false);

    const query = useKurumBonusTanimListesiQuery({ aktifGetir: state.aktifGetir, iptalGosterme: state.iptalGosterme }, { refetchOnMountOrArgChange: true });
    const [iptalEt, iptalEtState] = useKurumBonusTanimSilMutation();

    const handlerEdit = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>, row: VwKurumbonustanimDTO): void => {
        dispatch(kurumBonusTanimlariSlice.actions.setActiveRow(row));
        dispatch(kurumBonusTanimlariSlice.actions.setOpen(true));
    }
    const handlerDelete = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>, row: VwKurumbonustanimDTO): void => {
        dispatch(kurumBonusTanimlariSlice.actions.setActiveRow(row));
        setIptal(true);
    }
    const handlerTesteGore = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>, row: VwKurumbonustanimDTO): void => {
        dispatch(kurumBonusTanimlariSlice.actions.setActiveRow(row));
        navigate("test-bazli");
    }
    const columns: IGridColDef<VwKurumbonustanimDTO>[] = [
        {
            field: "kurumAdi",
            headerName: translate(translatePath, "Kurum Adı"),
            flex: 1,
            minWidth: 250,
        },
        {
            field: "bonusYontem",
            headerName: translate(translatePath, "Bonus Yöntem"),
            width: 150,
            type: "singleSelect",
            valueOptions: parametre.filter((f) => f.grupId === ParametreGrupEnum.KurumBonusYontem).map((m) => { return { value: m.key, label: m.value } as ValueOptions; })
        },
        {
            field: "oran",
            headerName: translate(translatePath, "Oran"),
            width: 100,
            type: "number",
            decimalScale: 2,
        },
        {
            field: "aktif",
            headerName: translate(translatePath, "Aktif"),
            width: 75,
            type: "boolean",
            editable: true,
            filterable: false,
            resizable: false,
            sortable: false,
            disableColumnMenu: true,
            valueGetter: (params) => { return params.row?.aktif === "E"; }
        },
        {
            field: "iptalTarihi",
            headerName: translate(translatePath, "İptal"),
            width: 75,
            type: "boolean",
            valueGetter: (params) => { return params.row?.iptalTarihi != null; }
        },
        {
            field: "action",
            headerName: translate(translatePath, "İşlemler"),
            width: 150,
            renderCell: (params) => {
                return (
                    <Stack direction="row">
                        {!params.row?.iptalTarihi && (
                            <>
                                <IconButton disabled={![2, 3]?.includes(params.row?.bonusYontem)} customType="numunereddet" toolTip="Teste Göre" onClick={(e) => { handlerTesteGore(e, params.row); }} />
                                <IconButton customType="edit" onClick={(e) => { handlerEdit(e, params.row); }} />
                                <IconButton customType="delete" loading={params.row?.id === iptalEtState.originalArgs?.id && iptalEtState.isLoading} onClick={(e) => { handlerDelete(e, params.row); }} />
                            </>
                        )}
                        <RecordInfo
                            id={params.row.id}
                            eklemeTarihi={params.row.eklemeTarih}
                            ekleyenKullanici={params.row.ekleyenKullanici}
                            guncellemeTarihi={params.row.guncellemeTarihi}
                            guncelleyenKullanici={params.row.guncelleyenKullanici}
                            iptalKullanici={params.row.iptalKullanici}
                            iptalTarihi={params.row.iptalTarihi}
                            iptalNedenGrupId={ParametreGrupEnum.TanimIptalNedenleri}
                            iptalNedenId={params.row.iptalNedenId}
                        />
                    </Stack>
                );
            },
        }
    ];

    const filterData = (data: Array<VwKurumbonustanimDTO>): Array<VwKurumbonustanimDTO> => {
        if (!Array.isArray(data)) return [];
        return data;
    };

    const handlerSearch = (event: React.MouseEvent<HTMLButtonElement>): void => {
        dispatch(api.util.invalidateTags(["KurumBonusTanimlari"]));
    }
    const handlerAdd = (event: React.MouseEvent<HTMLButtonElement>): void => {
        dispatch(kurumBonusTanimlariSlice.actions.setActiveRow(new VwKurumbonustanimDTO()));
        dispatch(kurumBonusTanimlariSlice.actions.setOpen(true));
    }

    const [guncelle, guncelleState] = useKurumBonusTanimGuncelleMutation();
    return (
        <>
            <Card>
                <CardContent>
                    <DataGrid
                        editMode="cell"
                        sx={{ height: "70vh" }}
                        pinnedColumns={{ right: ["action"] }}
                        searchButton={<Button customType="search" loading={query.isFetching} onClick={handlerSearch} />}
                        addButton={<Button customType="add" onClick={handlerAdd} />}
                        loading={query.isFetching}
                        rows={filterData(query.data)}
                        columns={columns}
                        onCellClick={(params) => {
                            if (params.field === "aktif" && !guncelleState.isLoading)
                                guncelle({ ...params.row, aktif: params.row?.aktif === "E" ? "H" : "E" }).unwrap().then(() => { }).catch(e => e);
                        }}
                        addHeaderColumn={
                            <Stack direction="row">
                                <Checkbox
                                    label={translate(translatePath, "Sadece Aktifler")}
                                    checked={state.aktifGetir === "E"}
                                    onChange={(e, checked) => { dispatch(kurumBonusTanimlariSlice.actions.setAktifGetir(checked ? "E" : "H")); }}
                                />
                                <Checkbox
                                    label={translate(translatePath, "İptalleri Göster")}
                                    checked={state.iptalGosterme === "E"}
                                    onChange={(e, checked) => { dispatch(kurumBonusTanimlariSlice.actions.setIptalGosterme(checked ? "E" : "H")); }}
                                />
                            </Stack>
                        }
                    />
                </CardContent>
            </Card>
            <IptalNedenSecim
                title={translate(translatePath, "Kurum Bonus Tanımı İptal")}
                parameterType={ParametreGrupEnum.TanimIptalNedenleri}
                loading={iptalEtState.isLoading}
                open={iptal}
                onClosed={() => { setIptal(false); }}
                onSelected={async (iptalNedenId, aciklama, iptalValue) => {
                    if (!iptalEtState.isLoading)
                        await iptalEt({ ...state.activeRow, iptalNedenId: iptalNedenId, iptalAciklama: aciklama }).unwrap().then(() => { }).catch(e => e);
                }}
            />
        </>
    );
};

export default KurumBonusTanimlariListe;